// Hook for managing cart items
import { useState, useEffect, useCallback } from "react";
import type { EnhancedCartItem } from "../types";
import { SessionStorageManager } from "~/utils/sessionStorageManager";
import { useShoppingCart } from "~/contexts/ShoppingCartContext";

export interface UseCartItemsReturn {
  enhancedCartItems: EnhancedCartItem[];
  setEnhancedCartItems: React.Dispatch<React.SetStateAction<EnhancedCartItem[]>>;
  itemNotes: Record<string, string[]>;
  setItemNotes: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  activeNoteInput: string | null;
  setActiveNoteInput: React.Dispatch<React.SetStateAction<string | null>>;
  addNote: (itemId: string, note: string) => void;
  updateNote: (itemId: string, noteIndex: number, newNote: string) => void;
  deleteNote: (itemId: string, noteIndex: number) => void;
  expandedItems: Set<string>;
  toggleItemExpansion: (itemId: string) => void;
  updateQuantity: (itemId: string, newQuantity: number) => void;
  removeItem: (itemId: string) => void;
  clearAllItems: () => void;
  orderNote: string;
  setOrderNote: React.Dispatch<React.SetStateAction<string>>;
  // Expanded notes state for collapse/expand UI
  expandedNotes: Set<string>;
  toggleNotesExpansion: (itemId: string) => void;
}

export const useCartItems = (): UseCartItemsReturn => {
  const { setTempQuantity, clearTempQuantities } = useShoppingCart();
  
  const [enhancedCartItems, setEnhancedCartItems] = useState<EnhancedCartItem[]>([]);
  const [itemNotes, setItemNotes] = useState<Record<string, string[]>>({});
  const [activeNoteInput, setActiveNoteInput] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [orderNote, setOrderNote] = useState<string>(() =>
    SessionStorageManager.getOrderNote()
  );

  // Load cart items from session storage
  const loadEnhancedCartItems = useCallback(() => {
    try {
      const itemCardsData = SessionStorageManager.getAllItems();
      let enhancedItems = Object.values(itemCardsData) as EnhancedCartItem[];

      // Sort items by ID
      enhancedItems = enhancedItems.sort((a, b) => {
        const regex = /([a-zA-Z]+)(\d*)/;
        const aMatch = a.itemId.match(regex);
        const bMatch = b.itemId.match(regex);
        if (aMatch && bMatch) {
          if (aMatch[1] !== bMatch[1]) {
            return aMatch[1].localeCompare(bMatch[1]);
          }
          const aNum = aMatch[2] ? parseInt(aMatch[2], 10) : 0;
          const bNum = bMatch[2] ? parseInt(bMatch[2], 10) : 0;
          return aNum - bNum;
        }
        return a.itemId.localeCompare(b.itemId);
      });

      setEnhancedCartItems(enhancedItems);

      // Load item notes arrays
      const noteEntries: Record<string, string[]> = {};
      enhancedItems.forEach((item) => {
        if (item.itemNotes && item.itemNotes.length > 0) {
          noteEntries[item.itemId] = [...item.itemNotes];
        }
      });
      setItemNotes(noteEntries);
      setOrderNote(SessionStorageManager.getOrderNote());
    } catch (error) {
      console.error("Error loading enhanced cart items:", error);
      setEnhancedCartItems([]);
      setItemNotes({});
      setOrderNote("");
    }
  }, []);

  // Initial load and storage event listeners
  useEffect(() => {
    loadEnhancedCartItems();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "restaurant-item-cards" || e.key === "restaurant-order-note") {
        loadEnhancedCartItems();
      }
    };

    const handleCartStorageUpdate = () => {
      loadEnhancedCartItems();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("cartStorageUpdate", handleCartStorageUpdate);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("cartStorageUpdate", handleCartStorageUpdate);
    };
  }, [loadEnhancedCartItems]);

  // Save order note to session storage
  useEffect(() => {
    const currentStoredNote = SessionStorageManager.getOrderNote();
    if (currentStoredNote === orderNote.trim()) {
      return;
    }
    SessionStorageManager.saveOrderNote(orderNote);
  }, [orderNote]);

  // Toggle item expansion (for variants/cards)
  const toggleItemExpansion = useCallback((itemId: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Toggle notes expansion (for collapse/expand notes section)
  const toggleNotesExpansion = useCallback((itemId: string) => {
    setExpandedNotes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Update session storage when item quantities change
  const updateSessionStorage = useCallback((itemId: string, newQuantity: number) => {
    try {
      const savedItemCards = sessionStorage.getItem("restaurant-item-cards");
      if (savedItemCards) {
        const itemCardsData = JSON.parse(savedItemCards);

        if (newQuantity <= 0) {
          delete itemCardsData[itemId];
        } else {
          if (itemCardsData[itemId]) {
            const originalQuantity = itemCardsData[itemId].totalQuantity;
            itemCardsData[itemId].totalQuantity = newQuantity;

            if (itemCardsData[itemId].cards && itemCardsData[itemId].cards.length > 0) {
              const cards = itemCardsData[itemId].cards;
              const quantityDiff = newQuantity - originalQuantity;

              if (quantityDiff > 0) {
                for (let i = 0; i < quantityDiff; i++) {
                  const newCard = {
                    cardId: `${itemId}-${Date.now()}-${cards.length + i + 1}`,
                    cardName: `${itemCardsData[itemId].itemName} #${cards.length + i + 1}`,
                    quantity: 1,
                    variants: [{ name: "", children: [], quantity: 1 }],
                  };
                  cards.push(newCard);
                }
              } else if (quantityDiff < 0) {
                const cardsToRemove = Math.abs(quantityDiff);
                cards.splice(-cardsToRemove, cardsToRemove);
              }
            }
          }
        }

        if (Object.keys(itemCardsData).length > 0) {
          sessionStorage.setItem("restaurant-item-cards", JSON.stringify(itemCardsData));
        } else {
          sessionStorage.removeItem("restaurant-item-cards");
        }

        window.dispatchEvent(
          new StorageEvent("storage", {
            key: "restaurant-item-cards",
            newValue: Object.keys(itemCardsData).length > 0 ? JSON.stringify(itemCardsData) : null,
            storageArea: sessionStorage,
          })
        );
      }
    } catch (error) {
      console.error("Error updating session storage:", error);
    }
  }, []);

  // Update quantity
  const updateQuantity = useCallback((itemId: string, newQuantity: number) => {
    updateSessionStorage(itemId, newQuantity);
    if (newQuantity <= 0) {
      setTempQuantity(itemId, 0);
    } else {
      setTempQuantity(itemId, newQuantity);
    }
  }, [updateSessionStorage, setTempQuantity]);

  // Remove item
  const removeItem = useCallback((itemId: string) => {
    updateSessionStorage(itemId, 0);
    setTempQuantity(itemId, 0);
    // Also remove notes for this item
    setItemNotes((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  }, [updateSessionStorage, setTempQuantity]);

  // Clear all items
  const clearAllItems = useCallback(() => {
    SessionStorageManager.clearAll();
    clearTempQuantities();
    setEnhancedCartItems([]);
    setItemNotes({});
    setOrderNote("");
  }, [clearTempQuantities]);

  // Add a new note to the item's notes array
  const addNote = useCallback((itemId: string, note: string) => {
    const trimmedNote = note.trim();
    if (!trimmedNote) return;
    
    // Update local state
    setItemNotes((prev) => {
      const existingNotes = prev[itemId] ?? [];
      return {
        ...prev,
        [itemId]: [...existingNotes, trimmedNote],
      };
    });
    
    // Persist to session storage
    SessionStorageManager.addItemNote(itemId, trimmedNote);
    
    setActiveNoteInput(null);
  }, []);

  // Update a specific note at index
  const updateNote = useCallback((itemId: string, noteIndex: number, newNote: string) => {
    const trimmedNote = newNote.trim();
    
    setItemNotes((prev) => {
      const existingNotes = prev[itemId] ?? [];
      if (noteIndex < 0 || noteIndex >= existingNotes.length) return prev;
      
      const updatedNotes = [...existingNotes];
      if (trimmedNote) {
        updatedNotes[noteIndex] = trimmedNote;
      } else {
        // Remove if empty
        updatedNotes.splice(noteIndex, 1);
      }
      
      if (updatedNotes.length === 0) {
        const next = { ...prev };
        delete next[itemId];
        return next;
      }
      
      return {
        ...prev,
        [itemId]: updatedNotes,
      };
    });
    
    // Persist to session storage
    SessionStorageManager.updateItemNoteAtIndex(itemId, noteIndex, trimmedNote);
    
    setActiveNoteInput(null);
  }, []);

  // Delete a specific note at index
  const deleteNote = useCallback((itemId: string, noteIndex: number) => {
    setItemNotes((prev) => {
      const existingNotes = prev[itemId] ?? [];
      if (noteIndex < 0 || noteIndex >= existingNotes.length) return prev;
      
      const updatedNotes = [...existingNotes];
      updatedNotes.splice(noteIndex, 1);
      
      if (updatedNotes.length === 0) {
        const next = { ...prev };
        delete next[itemId];
        return next;
      }
      
      return {
        ...prev,
        [itemId]: updatedNotes,
      };
    });
    
    // Persist to session storage
    SessionStorageManager.deleteItemNote(itemId, noteIndex);
  }, []);

  return {
    enhancedCartItems,
    setEnhancedCartItems,
    itemNotes,
    setItemNotes,
    activeNoteInput,
    setActiveNoteInput,
    addNote,
    updateNote,
    deleteNote,
    expandedItems,
    toggleItemExpansion,
    updateQuantity,
    removeItem,
    clearAllItems,
    orderNote,
    setOrderNote,
    expandedNotes,
    toggleNotesExpansion,
  };
};
